const samplePrompts = [
  { q: 'Suggest a reply to this customer asking about cancellation policy', icon: 'ri-reply-line' },
  { q: 'Draft a follow-up message for Sara — no reply in 3 days', icon: 'ri-time-line' },
  { q: 'Summarise Ahmed\'s booking history and preferences', icon: 'ri-file-list-3-line' },
  { q: 'What upsells can I offer for a Maldives honeymoon?', icon: 'ri-lightbulb-line' },
];

export default function AIAssistantModule() {
  return (
    <section id="ai-assistant" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                <i className="ri-robot-2-line text-teal-500" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Module 03</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 leading-tight tracking-tight mb-4">
              AI Assistant for<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">Your Internal Workflow</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-6">
              Think of it as having a brilliant travel-industry-trained colleague always by your side. Ask anything — drafting a reply, summarising a customer&apos;s history, suggesting upsells, or handling objections. Your agents work faster and smarter.
            </p>
            <ul className="flex flex-col gap-4">
              {[
                { icon: 'ri-draft-line', title: 'Draft messages & replies', desc: 'Type what you want to say, the AI writes it professionally. Review and send in seconds.' },
                { icon: 'ri-customer-service-2-line', title: 'Customer context at a glance', desc: 'Ask the AI to summarise any customer\'s history, preferences, and past conversations instantly.' },
                { icon: 'ri-shopping-bag-3-line', title: 'Upsell & package suggestions', desc: 'Based on the customer\'s trip type and budget, AI recommends relevant add-ons, upgrades, and packages.' },
                { icon: 'ri-question-answer-line', title: 'Objection handling scripts', desc: 'Not sure how to respond to a tricky question? The AI gives you multiple approaches to handle it confidently.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-teal-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: AI chat panel mockup */}
          <div className="bg-gray-950 rounded-2xl overflow-hidden border border-gray-800">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-md flex items-center justify-center">
                <i className="ri-robot-2-line text-white text-xs" />
              </div>
              <p className="text-xs font-semibold text-white">TreWaz AI Assistant</p>
              <span className="ml-auto text-[10px] bg-teal-900/50 text-teal-400 px-2 py-0.5 rounded-full">Agent workspace</span>
            </div>
            <div className="p-4 space-y-3">
              {/* Quick prompt chips */}
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Quick prompts</p>
              <div className="grid grid-cols-2 gap-2">
                {samplePrompts.map((p) => (
                  <button
                    key={p.q}
                    type="button"
                    className="bg-gray-800/60 border border-gray-700 rounded-lg p-2.5 text-left hover:border-teal-700 hover:bg-teal-900/20 transition-colors cursor-pointer"
                  >
                    <i className={`${p.icon} text-teal-400 text-xs block mb-1.5`} />
                    <p className="text-[11px] text-gray-300 leading-snug">{p.q}</p>
                  </button>
                ))}
              </div>

              {/* Sample AI reply */}
              <div className="mt-4 bg-gray-800/40 rounded-xl p-3 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                    <i className="ri-robot-2-line text-white text-[10px]" />
                  </div>
                  <p className="text-[10px] font-medium text-teal-400">AI Reply Draft</p>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  &ldquo;Hi Sara! We completely understand — life happens. Our cancellation policy allows a full refund up to 30 days before departure. Would you prefer to reschedule instead? I can check availability for later dates right away! 😊&rdquo;
                </p>
                <div className="flex gap-2 mt-3">
                  <button type="button" className="text-[10px] bg-teal-600 text-white px-3 py-1 rounded-md cursor-pointer whitespace-nowrap hover:bg-teal-700 transition-colors">
                    Send via WhatsApp
                  </button>
                  <button type="button" className="text-[10px] bg-gray-700 text-gray-300 px-3 py-1 rounded-md cursor-pointer whitespace-nowrap hover:bg-gray-600 transition-colors">
                    Edit first
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
